/**
 * Configurable Tax Calculation Engine for Naeem Builder ERP
 * Compliant with FBR/PRA/SRB rules and UBL Enterprise Framework.
 * Employs decimal-safe rounding (Half-Up) without IEEE 754 floating point leakage.
 */

export interface TaxCalculationResult {
  ruleCode: string;
  ruleVersion: number;
  taxType: string;
  taxableBasis: number;
  taxRate: number;
  calculatedTaxAmount: number;
  grossAmount: number;
  netPayableOrReceivable: number;
  roundingMethod: 'HALF_UP';
  breakdown: Record<string, number>;
  exemptionApplied: boolean;
  exemptionReason?: string;
}

/**
 * Decimal-safe rounding to specified decimal places (standard Half-Up).
 */
export function roundToDecimals(value: number | string, decimals: number = 2): number {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 0;
  // Use exponential notation to avoid binary floating point representation quirks
  return Number(Math.round(Number(num + 'e' + decimals)) + 'e-' + decimals);
}

/**
 * Format currency string to exactly 2 decimals
 */
export function formatDecimal(value: number | string): string {
  return roundToDecimals(value).toFixed(2);
}

/**
 * 1. Calculate UBL Sales Invoice Tax (16% GST)
 * Basis: Subtotal before GST.
 */
export function calculateSalesInvoiceTax(
  subtotal: number | string,
  configuredGstRate: number = 16.0
): {
  subtotal: number;
  gstRate: number;
  gstAmount: number;
  grandTotal: number;
} {
  const cleanSubtotal = roundToDecimals(subtotal);
  const gstAmount = roundToDecimals((cleanSubtotal * configuredGstRate) / 100);
  const grandTotal = roundToDecimals(cleanSubtotal + gstAmount);

  return {
    subtotal: cleanSubtotal,
    gstRate: configuredGstRate,
    gstAmount,
    grandTotal,
  };
}

/**
 * 2. Calculate UBL Payment Advice 14% Withholding Tax
 * Basis: CONSOLIDATED AMOUNT (Subtotal + GST).
 * Formula: ConsolidatedBasis = TotalSubtotal + TotalGST
 *          WithholdingAmount = ConsolidatedBasis * 14%
 *          ExpectedNet = ConsolidatedBasis - WithholdingAmount - OtherDeductions
 */
export interface CoveredInvoiceInput {
  id: number;
  invoiceNumber: string;
  subtotal: number | string;
  taxAmount: number | string;
  grandTotal?: number | string;
}

export interface UblAdviceCalculationResult {
  totalSubtotal: number;
  totalGst: number;
  consolidatedBasis: number;
  withholdingRate: number;
  consolidatedWithholding: number;
  otherDeductions: number;
  expectedNetPayment: number;
  allocatedInvoices: Array<{
    id: number;
    invoiceNumber: string;
    subtotal: number;
    taxAmount: number;
    grandTotal: number;
    allocatedWht: number;
    allocatedNet: number;
  }>;
}

export function calculateUblPaymentAdvice(
  invoices: CoveredInvoiceInput[],
  withholdingRate: number = 14.0,
  otherDeductions: number = 0.0
): UblAdviceCalculationResult {
  let totalSubtotal = 0;
  let totalGst = 0;

  const normalized = invoices.map((inv) => {
    const sub = roundToDecimals(inv.subtotal);
    const gst = roundToDecimals(inv.taxAmount);
    const grand = roundToDecimals(sub + gst);
    totalSubtotal = roundToDecimals(totalSubtotal + sub);
    totalGst = roundToDecimals(totalGst + gst);
    return {
      id: inv.id,
      invoiceNumber: inv.invoiceNumber,
      subtotal: sub,
      taxAmount: gst,
      grandTotal: grand,
    };
  });

  const consolidatedBasis = roundToDecimals(totalSubtotal + totalGst);
  const consolidatedWithholding = roundToDecimals((consolidatedBasis * withholdingRate) / 100);
  const cleanOtherDeductions = roundToDecimals(otherDeductions);
  const expectedNetPayment = roundToDecimals(consolidatedBasis - consolidatedWithholding - cleanOtherDeductions);

  // Allocate withholding proportionally across covered invoices
  let allocatedSum = 0;
  const allocatedInvoices = normalized.map((inv, idx) => {
    let allocatedWht: number;
    if (idx === normalized.length - 1) {
      // Plug difference in last item to guarantee exact cents match
      allocatedWht = roundToDecimals(consolidatedWithholding - allocatedSum);
    } else {
      const proportion = consolidatedBasis > 0 ? inv.grandTotal / consolidatedBasis : 0;
      allocatedWht = roundToDecimals(consolidatedWithholding * proportion);
      allocatedSum = roundToDecimals(allocatedSum + allocatedWht);
    }
    const allocatedNet = roundToDecimals(inv.grandTotal - allocatedWht);
    return {
      ...inv,
      allocatedWht,
      allocatedNet,
    };
  });

  return {
    totalSubtotal,
    totalGst,
    consolidatedBasis,
    withholdingRate,
    consolidatedWithholding,
    otherDeductions: cleanOtherDeductions,
    expectedNetPayment,
    allocatedInvoices,
  };
}

/**
 * 3. Calculate Supplier Bill Tax & Withholding
 * Handles:
 *  - Registered suppliers: GST 18%, WHT 5.5% (deducted from net payable)
 *  - Unregistered / No GST Invoice: 11% WHT recorded separately without reducing purchase cost or agreed bill
 *  - Verified Exempt Supplier: 0% WHT (requires valid verified exemption certificate)
 */
export interface SupplierTaxCalculationInput {
  grossBeforeTax: number | string;
  supplierCategory: 'REGISTERED' | 'UNREGISTERED' | 'IMPORTER' | 'MANUFACTURER' | 'CONTRACTOR' | 'SERVICE_PROVIDER' | string;
  hasGstInvoice: boolean;
  hasVerifiedExemptionDoc: boolean;
  exemptionValid?: boolean;
  configuredGstRate?: number; // default 18.0
  otherCharges?: number | string;
}

export interface SupplierBillTaxResult {
  grossBeforeTax: number;
  gstRate: number;
  gstAmount: number;
  otherCharges: number;
  grossTotal: number;
  withholdingRate: number;
  withholdingAmount: number;
  netPayable: number;
  isUnregisteredLocalPurchase: boolean;
  withholdingAccountedSeparately: boolean;
  exemptionApplied: boolean;
  taxClassification: string;
}

export function calculateSupplierBillTaxes(input: SupplierTaxCalculationInput): SupplierBillTaxResult {
  const grossBeforeTax = roundToDecimals(input.grossBeforeTax);
  const otherCharges = roundToDecimals(input.otherCharges || 0);

  const isExempt = Boolean(input.hasVerifiedExemptionDoc && input.exemptionValid);
  const isRegistered = input.supplierCategory === 'REGISTERED' || (input.hasGstInvoice && input.supplierCategory !== 'UNREGISTERED');

  let gstRate = 0;
  let gstAmount = 0;
  let withholdingRate = 0;
  let withholdingAmount = 0;
  let netPayable = 0;
  let isUnregisteredLocalPurchase = false;
  let withholdingAccountedSeparately = false;
  let taxClassification = 'STANDARD_PURCHASE';

  if (isExempt) {
    // Verified Exemption: 0% WHT
    gstRate = input.hasGstInvoice ? roundToDecimals(input.configuredGstRate ?? 18.0) : 0;
    gstAmount = roundToDecimals((grossBeforeTax * gstRate) / 100);
    const grossTotal = roundToDecimals(grossBeforeTax + gstAmount + otherCharges);
    withholdingRate = 0;
    withholdingAmount = 0;
    netPayable = grossTotal;
    taxClassification = 'EXEMPT_WITH_VERIFIED_CERTIFICATE';

    return {
      grossBeforeTax,
      gstRate,
      gstAmount,
      otherCharges,
      grossTotal,
      withholdingRate,
      withholdingAmount,
      netPayable,
      isUnregisteredLocalPurchase: false,
      withholdingAccountedSeparately: false,
      exemptionApplied: true,
      taxClassification,
    };
  }

  if (isRegistered && input.hasGstInvoice) {
    // Registered supplier with valid GST invoice
    gstRate = roundToDecimals(input.configuredGstRate ?? 18.0);
    gstAmount = roundToDecimals((grossBeforeTax * gstRate) / 100);
    const grossTotal = roundToDecimals(grossBeforeTax + gstAmount + otherCharges);
    
    // Registered WHT: 5.5% on grossBeforeTax
    withholdingRate = 5.5;
    withholdingAmount = roundToDecimals((grossBeforeTax * withholdingRate) / 100);
    
    // Deducted from payment to supplier
    netPayable = roundToDecimals(grossTotal - withholdingAmount);
    taxClassification = 'REGISTERED_GST_SUPPLIER_WHT_5.5';

    return {
      grossBeforeTax,
      gstRate,
      gstAmount,
      otherCharges,
      grossTotal,
      withholdingRate,
      withholdingAmount,
      netPayable,
      isUnregisteredLocalPurchase: false,
      withholdingAccountedSeparately: false,
      exemptionApplied: false,
      taxClassification,
    };
  } else {
    // Local purchase without GST invoice (unregistered supplier)
    // Rule: 11% WHT recorded separately without reducing agreed bill/purchase cost.
    gstRate = 0;
    gstAmount = 0;
    const grossTotal = roundToDecimals(grossBeforeTax + otherCharges);
    
    // 11% WHT rate
    withholdingRate = 11.0;
    withholdingAmount = roundToDecimals((grossBeforeTax * withholdingRate) / 100);
    
    // For unregistered local purchase: agreed bill is preserved, withholding liability is tracked separately
    isUnregisteredLocalPurchase = true;
    withholdingAccountedSeparately = true;
    netPayable = grossTotal; // Net payable to vendor equals agreed gross total; withholding is funded/borne or recorded per rule
    taxClassification = 'UNREGISTERED_LOCAL_PURCHASE_WHT_11';

    return {
      grossBeforeTax,
      gstRate,
      gstAmount,
      otherCharges,
      grossTotal,
      withholdingRate,
      withholdingAmount,
      netPayable,
      isUnregisteredLocalPurchase,
      withholdingAccountedSeparately,
      exemptionApplied: false,
      taxClassification,
    };
  }
}
